import type { CRBattle, CRPlayerCard } from '@/types/qbot';
import { resolvePov } from '@/lib/battlePov';
import { modeBucket, MODE_ORDER, type ModeBucket } from '@/lib/battleMode';

export interface ModeStat { bucket: ModeBucket; wins: number; losses: number; draws: number; n: number; winRatePct: number; }
export interface DeckStat { signature: string; cards: CRPlayerCard[]; wins: number; losses: number; draws: number; n: number; winRatePct: number; }
export interface FacedCard { id: number; name: string; iconUrl?: string; count: number; }
export interface BattleStats {
  total: number; wins: number; losses: number; draws: number; winRatePct: number;
  streak: number; crownsFor: number; crownsAgainst: number; crownDiff: number;
  byMode: ModeStat[]; byDeck: DeckStat[];
  trophy: { points: number[]; net: number }; facedCards: FacedCard[];
}

/** Win rate excludes draws from the denominator (CR convention). */
function wr(wins: number, losses: number): number {
  const decisive = wins + losses;
  return decisive ? Math.round((wins / decisive) * 100) : 0;
}

/**
 * Compute all window analytics from the ~25-battle log. `battles[0]` is the
 * newest (Supercell order). Battles where POV can't be resolved are skipped.
 * Pure — same input always yields the same output.
 */
export function computeStats(battles: CRBattle[], playerTag: string): BattleStats {
  let wins = 0, losses = 0, draws = 0, crownsFor = 0, crownsAgainst = 0;
  const modeAgg = new Map<ModeBucket, { w: number; l: number; d: number }>();
  const deckAgg = new Map<string, { cards: CRPlayerCard[]; w: number; l: number; d: number }>();
  const faced = new Map<number, FacedCard>();
  const results: ('win' | 'loss' | 'draw')[] = []; // newest-first, for streak
  const trophyDeltas: number[] = []; // oldest-first, ranked battles only

  for (const battle of battles) {
    const pov = resolvePov(battle, playerTag);
    if (!pov) continue;
    const { me, opp, result } = pov;

    results.push(result);
    if (result === 'win') wins++; else if (result === 'loss') losses++; else draws++;
    crownsFor += me.crowns; crownsAgainst += opp.crowns;

    const mb = modeBucket(battle);
    const m = modeAgg.get(mb) ?? { w: 0, l: 0, d: 0 };
    m[result === 'win' ? 'w' : result === 'loss' ? 'l' : 'd']++;
    modeAgg.set(mb, m);

    const sig = me.cards.map((c) => c.id).sort((a, b) => a - b).join(',');
    if (sig) {
      const d = deckAgg.get(sig) ?? { cards: me.cards, w: 0, l: 0, d: 0 };
      d[result === 'win' ? 'w' : result === 'loss' ? 'l' : 'd']++;
      deckAgg.set(sig, d);
    }

    for (const card of opp.cards) {
      const f = faced.get(card.id) ?? { id: card.id, name: card.name, iconUrl: card.iconUrls?.medium, count: 0 };
      f.count++;
      faced.set(card.id, f);
    }

    if (typeof me.trophyChange === 'number') trophyDeltas.unshift(me.trophyChange); // reverse → oldest-first
  }

  // Streak: consecutive same non-draw result from the newest battle.
  let streak = 0;
  if (results[0] && results[0] !== 'draw') {
    const sign = results[0] === 'win' ? 1 : -1;
    for (const r of results) { if (r === results[0]) streak += sign; else break; }
  }

  // Trophy: cumulative net-delta series, oldest-first (each point = running sum of trophyChange so far). net = final running total.
  const points: number[] = [];
  let running = 0;
  for (const delta of trophyDeltas) { running += delta; points.push(running); }

  const byMode: ModeStat[] = MODE_ORDER
    .filter((b) => modeAgg.has(b))
    .map((b) => {
      const a = modeAgg.get(b)!;
      return { bucket: b, wins: a.w, losses: a.l, draws: a.d, n: a.w + a.l + a.d, winRatePct: wr(a.w, a.l) };
    });

  const byDeck: DeckStat[] = deckAgg.size > 1
    ? [...deckAgg.entries()]
        .map(([signature, a]) => ({ signature, cards: a.cards, wins: a.w, losses: a.l, draws: a.d, n: a.w + a.l + a.d, winRatePct: wr(a.w, a.l) }))
        .sort((x, y) => y.n - x.n)
    : [];

  const facedCards = [...faced.values()].sort((x, y) => y.count - x.count).slice(0, 8);
  const total = wins + losses + draws;

  return {
    total, wins, losses, draws, winRatePct: wr(wins, losses),
    streak, crownsFor, crownsAgainst, crownDiff: crownsFor - crownsAgainst,
    byMode, byDeck, trophy: { points, net: running }, facedCards,
  };
}
